import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import __vue_component__ from '../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__$1 from '../symbols/X.vue.js';
import __vue_component__$2 from '../ui/Tag/Tag.vue.js';
import { useBrowserWidth } from '../../composables/browser.js';
import '@caresend/utils';
import 'lodash.isequal';
import '../../router/helpers/initRouter.js';
import '@caresend/types';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';

var script = defineComponent({
    name: 'Alert',
    components: {
        ButtonComponent: __vue_component__,
        X: __vue_component__$1,
        Tag: __vue_component__$2,
    },
    model: {
        prop: 'isOpened',
        event: 'update',
    },
    props: {
        bodyClasses: {
            type: String,
            default: '',
        },
        bodyCustomStyles: {
            type: String,
            default: '',
        },
        // Turn on bodyVisible to use phoneNumberInput in a small alert
        bodyVisible: Boolean,
        /** TODO: Remove once alerts across all content bodies are fixed */
        bodyScroll: Boolean,
        /** TODO: Remove once alerts across all content bodies are fixed */
        bodyScrollReverse: Boolean,
        bottom: Boolean,
        confirmButtonFullWidth: {
            type: Boolean,
            default: true,
        },
        confirmDanger: Boolean,
        /**
         * Pass `true` to disable the button. Pass a string to disable the button
         * and show a tooltip on hover.
         */
        confirmDisabled: {
            type: [Boolean, String],
            default: false,
        },
        confirmTo: {
            type: [String, Object],
            default: undefined,
        },
        containerClasses: {
            type: String,
            default: '',
        },
        debugMode: Boolean,
        disableDismiss: Boolean,
        fullScreen: Boolean,
        headerBorder: Boolean,
        headerClasses: {
            type: String,
            default: '',
        },
        hideCancelButton: Boolean,
        hideCloseButton: Boolean,
        hideConfirmButton: Boolean,
        hideFooter: Boolean,
        isLoading: Boolean,
        isOpened: Boolean,
        /** TODO: Remove once alerts across all content bodies are fixed */
        normalPadding: Boolean,
        textButton: {
            type: String,
            default: 'Confirm',
        },
        textButtonCancel: {
            type: String,
            default: 'Cancel',
        },
        title: {
            type: String,
            default: '',
        },
        /**
         * Only on mobile, on desktop the footer will be at the bottom of the modal
         * which is in the center of the viewport
         */
        fixedFooter: {
            type: Boolean,
            default: true,
        },
        /** Only on mobile, on desktop the header will be at the top of the modal */
        fixedHeader: {
            type: Boolean,
            default: true,
        },
    },
    setup(props, { emit, slots }) {
        const isOpenedState = ref(props.isOpened);
        const noBody = computed(() => !slots.default);
        const hasFooter = computed(() => slots.footer !== undefined);
        const containerClass = computed(() => {
            const names = [];
            if (props.bodyVisible) {
                names.push('Alert__modal-container--visible');
            }
            else {
                names.push('Alert__modal-container--scroll');
            }
            if (props.containerClasses) {
                names.push(props.containerClasses);
            }
            if (props.bottom) {
                names.push('Alert__modal-container--bottom');
            }
            else {
                names.push('modal-card');
            }
            if (noBody.value) {
                names.push('Alert__modal-container--no-body');
            }
            if (props.headerBorder) {
                names.push('Alert__modal-container--header-border');
            }
            return names;
        });
        const bodyClass = computed(() => {
            const names = [];
            if (props.normalPadding) {
                names.push('Alert__body--normal-padding');
            }
            if (props.bodyVisible) {
                names.push('Alert__body--overflow-visible');
            }
            if (props.bodyScrollReverse) {
                names.push('Alert__body--scroll-reverse');
            }
            if (props.hideFooter) {
                names.push('Alert__body--hide-footer');
            }
            return [...names, props.bodyClasses];
        });
        const bodyRef = ref(null);
        const footerRef = ref(null);
        const headerRef = ref(null);
        const scrollY = ref(0);
        const displayHeaderBorder = ref(false);
        const displayFooterBorder = ref(false);
        const updateBorders = () => {
            if (bodyRef.value) {
                const bodyContentHeight = bodyRef.value.scrollHeight;
                const bodyHeight = bodyRef.value.clientHeight;
                if (scrollY.value === 0 && props.bodyScrollReverse) {
                    displayHeaderBorder.value = true;
                }
                else if (scrollY.value > 0) {
                    displayHeaderBorder.value = true;
                }
                else {
                    displayHeaderBorder.value = false;
                }
                if (scrollY.value === 0 && props.bodyScrollReverse) {
                    displayFooterBorder.value = false;
                }
                else if ((scrollY.value + bodyHeight) < bodyContentHeight) {
                    displayFooterBorder.value = true;
                }
                else {
                    displayFooterBorder.value = false;
                }
            }
        };
        const onBodyScroll = (event) => {
            var _a;
            scrollY.value = (_a = event.target.scrollTop) !== null && _a !== void 0 ? _a : 0;
            updateBorders();
        };
        watch(() => [bodyRef.value, footerRef.value], () => {
            // If the footer is fixed, add padding to the bottom of the main content to prevent overlap
            if (props.fixedFooter
                && bodyRef.value
                && footerRef.value) {
                const footerHeight = footerRef.value.clientHeight;
                footerRef.value.style.display = 'none';
                bodyRef.value.style.paddingBottom = `${footerHeight}px`;
                footerRef.value.style.display = 'block';
            }
        }, { immediate: true });
        watch(() => [bodyRef.value, headerRef.value], () => {
            // If the header is fixed, add padding to the top of the main content to prevent overlap
            if (props.fixedHeader
                && bodyRef.value
                && headerRef.value) {
                const headerHeight = headerRef.value.clientHeight;
                bodyRef.value.style.paddingTop = `${headerHeight}px`;
            }
        }, { immediate: true });
        const headerClassNames = computed(() => {
            const names = [];
            if (props.headerClasses) {
                names.push(props.headerClasses);
            }
            if (displayHeaderBorder.value || props.headerBorder) {
                names.push('Alert__header--with-border');
            }
            return names;
        });
        const footerClasses = computed(() => {
            const names = [];
            names.push('Alert__footer--fixed');
            if (displayFooterBorder.value) {
                names.push('Alert__footer--with-border');
            }
            return names;
        });
        const confirmButtonStyle = computed(() => props.confirmDanger ? 'danger' : 'primary');
        const modalClass = computed(() => {
            const names = [];
            if (props.bottom) {
                names.push('Alert__modal--bottom');
            }
            return names;
        });
        watch(() => props.isOpened, (next) => {
            isOpenedState.value = next;
        });
        watch(isOpenedState, (next) => {
            emit('update', next);
            if (!next)
                emit('close');
        });
        const emitCancel = () => { emit('cancel'); };
        const closeModal = () => {
            emitCancel();
            isOpenedState.value = false;
        };
        onMounted(() => {
            updateBorders();
        });
        return {
            ...useBrowserWidth(),
            bodyClass,
            bodyRef,
            closeModal,
            confirmButtonStyle,
            containerClass,
            emitCancel,
            footerClasses,
            footerRef,
            hasFooter,
            headerClassNames,
            headerRef,
            isOpenedState,
            modalClass,
            noBody,
            onBodyScroll,
        };
    },
});

export { script as default };
